import Vue from "vue";
import moment from "moment";
import { Component, Prop, Watch } from "vue-property-decorator";
import { mapState } from "vuex";
import { JourFerieDto } from "../../../dto/GenApi.iNotPaie.Dto/JourFerieDto";
import { SalariePlanningEquipeDto } from "../../../dto/GenApi.iNotPaie.Dto/SalariePlanningEquipeDto";
import { ServicesFactoryInstance } from "../../Controls/ServicesFactory";
import dateHelper from "../../../Helper/dateHelper";
import stringHelper from "../../../Helper/stringHelper";
import { CalendarDay } from "../../../shared/interfaces/calendar.interface";
import { NotificationServiceInstance } from "../../../shared/services/notification.service";
import { EventBus } from "../../Controls/EventBus";
import EventConstants from "../../Controls/EventConstants";
import { ParametresInstance } from "../../Controls/Parameters";
import { ModePlanningEquipeEnum } from "../../../dto/GenApi.iNotPaie.Dto/ModePlanningEquipeEnum";
import { MultiSelectOption } from "../../../shared/components/dropDownSelect/dropDownSelect";

@Component({
    components: {
        ModalAjoutConge: require("../modalAjoutConge/modalAjoutConge.vue.html"),
        NavigationMois: require("../calendrierMensuel/navigationMois/navigationMois.vue.html"),
        AbsencePlanningEquipe: require("./absencePlanningEquipe/absencePlanningEquipe.vue.html"),
        DropDownSelect: require("../../../shared/components/dropDownSelect/dropDownSelect.vue.html")
    },
    computed: {
        ...mapState("ParametersStore", ["storeVariableListeJoursFeries"])
    }
})
export default class CalendrierEquipeComponent extends Vue {
    @Prop({ default: "AutreSalaries" }) public activeTab!: string;

    @Watch("activeTab")
    public async onActiveTabChanged(): Promise<void> {
        await this.loadData();
    }

    public readonly AUCUN_SERVICE = "AUCUN_SERVICE";

    /** Jours fériés (store Vuex), comme CalendrierMensuel */
    public storeVariableListeJoursFeries!: JourFerieDto[];
    public currentMonth = moment().month();
    public currentYear = moment().year();
    public isLoading = true;
    public salariesEquipe: SalariePlanningEquipeDto[] = [];
    public salariesEquipeFiltered: SalariePlanningEquipeDto[] = [];
    public monPlaning: SalariePlanningEquipeDto | null = null;

    /** Salarié recherché */
    public nameSearch = "";

    public listeServices: MultiSelectOption[] = [];
    public selectedServices: string[] = [];
    public collapsedServices: string[] = [];

    public async mounted(): Promise<void> {
        EventBus.$on(EventConstants.UPDATE_CONGES, () => this.loadData());

        await this.loadData();
    }

    /**
     * Gère le changement de sélection des services
     * @param listServiceSelectionnes - La liste des services sélectionnés
     * @returns void
     */
    public onServiceFilterChanged(listServiceSelectionnes: string[]): void {
        // Collapse les groupes de services non sélectionnés
        this.collapsedServices = this.collapsedServices.filter(key => listServiceSelectionnes.includes(key));
        // Met à jour les services sélectionnés
        this.selectedServices = listServiceSelectionnes;
        // Filtre la liste des salariés
        this.filterSalarieList();
    }

    /**
     * Collapse ou expand le groupe de services
     * @param key - La clé du groupe de services
     * @returns void
     */
    public toggleServiceGroup(key: string): void {
        if (this.collapsedServices.includes(key)) {
            this.collapsedServices = this.collapsedServices.filter(k => k !== key);
        } else {
            this.collapsedServices = [...this.collapsedServices, key];
        }
    }

    /**
     * Retourne true si le groupe de services est collapssé
     * @param key - La clé du groupe de services
     * @returns boolean
     */
    public isServiceGroupCollapsed(key: string): boolean {
        return this.collapsedServices.includes(key);
    }

    /**
     * Initialise la liste des services pour le filtre (dédoublonnée et triée)
     * @returns void
     */
    private setServicesFilter(): void {
        // monPlaning est inclus dans les sources pour que son service apparaisse même s'il en est le seul membre
        const allSalaries = this.monPlaning ? [...this.salariesEquipe, this.monPlaning] : this.salariesEquipe;

        const services = allSalaries
            // Dédoublonnage des services
            .filter((s, i, arr) => s.CodeService != null && arr.findIndex(x => x.CodeService === s.CodeService) === i)
            // Mapping des services
            .map(s => ({
                value: s.CodeService!,
                label: s.ServiceLibelle || s.CodeService!
            }))
            // Tri des services alphabétiquement
            .sort((a, b) => a.label.localeCompare(b.label, "fr"));

        const hasAucunService = this.salariesEquipe.some(s => s.CodeService == null) || this.monPlaning?.CodeService == null;
        if (hasAucunService) {
            services.push({ value: this.AUCUN_SERVICE, label: "Aucun service" });
        }

        this.listeServices = services;
    }

    /**
     * Charge les données de l'équipe
     * @returns Promise<void>
     */
    private async loadData(): Promise<void> {
        this.isLoading = true;
        try {
            const dates = this.getDateRange();
            await this.loadEquipeSalaries(dates.dateDebut, dates.dateFin);
            this.filterSalarieList();
        } finally {
            this.isLoading = false;
        }
    }

    /**
     * Retourne la période de dates à charger
     * @returns { dateDebut: string; dateFin: string }
     */
    private getDateRange(): { dateDebut: string; dateFin: string } {
        const calDays = this.calendarDays;
        const dateDebut = moment(calDays[0].date).utcOffset(0, true).toDate();
        const dateFin = moment(calDays[calDays.length - 1].date).utcOffset(0, true).toDate();

        return {
            dateDebut: dateHelper.formatDateForUrl(dateDebut),
            dateFin: dateHelper.formatDateForUrl(dateFin)
        };
    }

    /**
     * Charge les salariés de l'équipe
     * @param dateDebut - La date de début
     * @param dateFin - La date de fin
     * @returns Promise<void>
     */
    private async loadEquipeSalaries(dateDebut: string, dateFin: string): Promise<void> {
        try {
            this.salariesEquipe = [];
            this.monPlaning = null;

            const mode = this.activeTab === "TousSalaries" ? ModePlanningEquipeEnum.Tous : ModePlanningEquipeEnum.Regroupement;
            const listSalaries = await ServicesFactoryInstance.AbsencesService.getAbsencesPlanningEquipe(dateDebut, dateFin, mode);

            listSalaries.forEach((elt: SalariePlanningEquipeDto) => {
                if (elt.IdSalarie === ParametresInstance.salarieConnecte?.IdSalarie) {
                    this.monPlaning = elt;
                } else {
                    this.salariesEquipe.push(elt);
                }
            });

            this.salariesEquipeFiltered = this.salariesEquipe;

            this.setServicesFilter();
        } catch {
            NotificationServiceInstance.SendFailed("Une erreur est survenue lors de la récupération des salariés.");
        }
    }

    /**
     * Filtre la liste des salariés affichés selon les services sélectionnés et la recherche par nom.
     * - Si aucun service sélectionné : tous les salariés, triés alphabétiquement, sans subheaders.
     * - Si des services sélectionnés : filtre sur ces services (recherche uniquement dans ce périmètre).
     */
    public filterSalarieList(): void {
        let salariesBase: SalariePlanningEquipeDto[];

        // Si aucun service sélectionné : tous les salariés
        if (this.selectedServices.length === 0) {
            salariesBase = [...this.salariesEquipe];
        } else {
            // Si des services sélectionnés : filtre sur ces services (recherche uniquement dans ce périmètre)
            salariesBase = this.salariesEquipe.filter(salarie => {
                if (salarie.CodeService == null) {
                    return this.selectedServices.includes(this.AUCUN_SERVICE);
                }

                return this.selectedServices.includes(salarie.CodeService);
            });
        }

        if (this.nameSearch !== "") {
            const searchNormalized = stringHelper.normalizeString(this.nameSearch);
            salariesBase = salariesBase.filter(salarie => {
                if (!salarie.NomPrenom) {
                    return false;
                }

                return stringHelper.normalizeString(salarie.NomPrenom).includes(searchNormalized);
            });
        }

        salariesBase.sort((a, b) => (a.NomPrenom || "").localeCompare(b.NomPrenom || "", "fr"));
        this.salariesEquipeFiltered = salariesBase;
    }

    /**
     * Retourne les groupes de salariés par service triés alphabétiquement
     * @returns Array de groupes de salariés par service triés alphabétiquement
     */
    public get salariesGroupes(): { key: string; codeService: string | null; libelleService: string; salaries: SalariePlanningEquipeDto[]; totalCount: number; isMonService: boolean }[] {
        const groupes = this.selectedServices.map(key => {
            // Si le service est le service du salarié connecté
            const isAucun = key === this.AUCUN_SERVICE;
            // Filtre les salariés par service
            const salaries = this.salariesEquipeFiltered.filter(s => (s.CodeService ?? this.AUCUN_SERVICE) === key);
            // Nombre total de salariés par service
            const totalCount = this.salariesEquipe.filter(s => (s.CodeService ?? this.AUCUN_SERVICE) === key).length;
            // True si le service est le service du salarié connecté
            const isMonService = isAucun ? this.monPlaning?.CodeService == null : this.monPlaning?.CodeService === key;
            // Nombre total de salariés par service + le salarié connecté si le service est le service du salarié connecté
            const totalCountWithMe = isMonService ? totalCount + 1 : totalCount;

            return {
                key, // La clé du groupe de services
                codeService: isAucun ? null : key, // Le code du service
                libelleService: this.listeServices.find(s => s.value === key)?.label || key, // Le libellé du service
                salaries, // Les salariés du groupe de services
                totalCount: totalCountWithMe, // Le nombre total de salariés du groupe de services
                isMonService // True si le service est le service du salarié connecté
            };
        });

        return groupes.filter(groupe => !this.nameSearch || groupe.salaries.length > 0).sort((a, b) => {
            if (a.codeService === null) {
                return 1;
            }

            if (b.codeService === null) {
                return -1;
            }

            return a.libelleService.localeCompare(b.libelleService, "fr");
        });
    }

    /**
     * Met à jour le mois et l'année actuelle
     * @param data Objet contenant le mois et l'année
     * @returns Promise<void>
     */
    public async updateMonth(data: { month: number, year: number }): Promise<void> {
        this.currentMonth = data.month;
        this.currentYear = data.year;
        await this.loadData();
    }

    /**
     * Moment actuel
     * @returns Moment actuel
     */
    public get currentMoment(): moment.Moment {
        return moment([this.currentYear, this.currentMonth, 1]);
    }

    /**
     * Nombre de jours dans le mois actuel
     * @returns Nombre de jours dans le mois
     */
    public get daysInMonth(): number {
        return this.currentMoment.daysInMonth();
    }

    /**
     * Computed
     * Génère tous les jours à afficher dans le calendrier
     * @returns Array de jours avec leurs informations
     */
    public get calendarDays(): CalendarDay[] {
        return Array.from({ length: this.daysInMonth }, (_, i): CalendarDay => {
            const date = moment(this.currentMoment).date(i + 1).toDate();
            return {
                day: i + 1,
                isCurrentMonth: true,
                isCurrentDay: dateHelper.isCurrentDay(date, this.currentMonth, this.currentYear),
                isWeekEndDay: dateHelper.isWeekend(date),
                isNonWorkingDay: dateHelper.isNonWorkingDay(date, this.storeVariableListeJoursFeries),
                date
            };
        });
    }

    /**
     * Libellé court du jour pour une date (LUN, MAR, ...)
     * @param day Jour à vérifier
     * @returns Libellé court du jour
     */
    public getWeekdayLabel(day: CalendarDay): string {
        return dateHelper.weekdaysShort()[moment(day.date).isoWeekday() - 1];
    }

    /** Numéros de semaine (1 à N selon le nombre de semaines dans calendarDays) */
    public get weekNumbers(): number[] {
        const n = Math.ceil(this.calendarDays.length / 7);
        return Array.from({ length: n }, (_, i) => i + 1);
    }

    /** Les 7 jours d'une semaine donnée (comme calendrierMensuel) */
    public getWeekDays(weekNumber: number): CalendarDay[] {
        const startIndex = (weekNumber - 1) * 7;
        return this.calendarDays.slice(startIndex, startIndex + 7);
    }

    /**
     * Retourne les légendes des absences
     * @returns Array des légendes des absences
     */
    public get calendarLegends(): { libelle: string, color: string }[] {
        const legendMap = new Map<string, { libelle: string, color: string }>();

        const allAbsence = [...(this.monPlaning?.Absences ?? []), ...this.salariesEquipe.flatMap((salarie) => salarie.Absences ?? [])];

        for (const absence of allAbsence) {
            const code = absence.CodeTypeAbsence as string;
            if (!legendMap.has(code) && absence.LibelleTypeAbsence) {
                legendMap.set(code, {
                    libelle: absence.LibelleTypeAbsence as string,
                    color: absence.Color as string
                });
            }
        }

        return Array.from(legendMap.values());
    }

}



// WEBPACK FOOTER //
// ./src/components/gestionDesAbsencesV2/calendrierEquipe/calendrierEquipe.ts